/** @jsx jsx */
import { jsx } from '@theme-ui/core';
import { IconUiPlus, ButtonGhost, LabelTag } from '@uberall/zenit';
import { useEffect, useState, useRef } from 'react';

import styles from './selection-list.module.css';
import Message from '../../Message';
import { SelectionListItem } from '../../../contexts/types';

type SelectionListProps = {
    title: string;
    subtitle: string;
    items: SelectionListItem[];
    removeItem: (id: string) => void;
    openSelectionModal: () => void;
};

const ITEMS_SHOWN_AMOUNT = 3;

const SelectionList = ({
    title,
    subtitle,
    items = [],
    removeItem,
    openSelectionModal,
}: SelectionListProps) => {
    const [itemsShown, setItemsShown] = useState(items.slice(0, ITEMS_SHOWN_AMOUNT));
    const [itemsShownCount, setItemsShownCount] = useState(ITEMS_SHOWN_AMOUNT);
    const prevItemsRef = useRef(items);

    useEffect(() => {
        // if new items have been added, then reset and show only the first 3 items
        if (items.length > prevItemsRef.current.length) {
            setItemsShown(items.slice(0, ITEMS_SHOWN_AMOUNT));
            setItemsShownCount(ITEMS_SHOWN_AMOUNT);
        } else {
            // if items have been removed, then show as many items as itemsShownCount has
            setItemsShown(items.slice(0, itemsShownCount));
        }

        prevItemsRef.current = items;
    }, [items, itemsShownCount]);

    const hasSelectedItems = items.length > 0;
    const displayShowMoreButton = items.length > itemsShown.length;

    const onShowMoreClick = () => {
        const itemsCount = itemsShown.length + ITEMS_SHOWN_AMOUNT;
        setItemsShownCount(itemsCount);
    };

    return (
        <div className={styles.container}>
            <div className={styles.titleAndButton}>
                <div className={styles.left}>
                    <h2 className={styles.title}>
                        <Message code={title} />
                    </h2>

                    <p className={styles.subtitle}>
                        <Message code={subtitle} />
                    </p>
                </div>
                <ButtonGhost
                    icon={<IconUiPlus size="large" className={styles.iconPlus} />}
                    onClick={openSelectionModal}
                    aria-label={subtitle}
                    sx={{ padding: 0 }}
                />
            </div>
            {hasSelectedItems && (
                <div className={styles.itemContainer}>
                    {itemsShown.map((item) => (
                        <LabelTag
                            key={item.title}
                            subText={item.subtitle}
                            onClick={() => removeItem(item.id)}
                            sx={{ backgroundColor: '#f7f7f7' }} // hardcoded grey.3
                        >
                            {item.title}
                        </LabelTag>
                    ))}
                    {displayShowMoreButton && (
                        <ButtonGhost
                            hasLinkStyles
                            className={styles.showAll}
                            onClick={onShowMoreClick}
                        >
                            <Message code="app.request.access.public.selection.list.show.more" />
                        </ButtonGhost>
                    )}
                </div>
            )}
        </div>
    );
};

export default SelectionList;
